import {
  Component,
  computed,
  forwardRef,
  input,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidatorFn,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';

import { TextComponent } from '../../typography/text/text.component';

@Component({
  selector: 'app-time-picker',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TextComponent
],
  templateUrl: './time-picker.component.html',
  styleUrls: ['./time-picker.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimePickerComponent),
      multi: true,
    }],
})
export class TimePickerComponent implements ControlValueAccessor {
  // --- Static Validator ---
  static isValidTime(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null; // Don't validate empty values, let `required` handle it
      }
      // Regex for HH:mm format
      const isValid = /^(?:2[0-3]|[01]?[0-9]):[0-5][0-9]$/.test(value);
      return isValid ? null : { invalidTime: true };
    };
  }

  // --- Inputs ---
  placeholder = input('Select a time');
  initialValue = input<string | undefined>(undefined);
  size = input<'sm' | 'md' | 'lg'>('md');
  variant = input<'outline' | 'ghost' | 'blend'>('outline');

  // --- State managed as signals ---
  private cleanValue = signal<string>(''); // The raw numeric string (e.g., '1830')
  private isFocused = signal(false);
  isDisabled = signal(false);
  isInvalid = signal(false);
  period: WritableSignal<'AM' | 'PM'> = signal('AM');

  // --- DYNAMIC CLASSES ---
  readonly containerClasses = computed(() => {
    const classes = [
      'input-base',
      `input-variant-${this.variant()}`,
      `input-size-${this.size()}`];
    if (this.isDisabled()) classes.push('opacity-60 cursor-not-allowed');
    if (this.isInvalid()) classes.push('input-error');
    return classes.join(' ');
  });

  readonly iconClasses = computed(() => {
    const classes = ['align-middle', 'justify-center'];

    if (this.size() === 'sm') classes.push('w-4 h-4');
    if (this.size() === 'md') classes.push('w-5 h-5');
    if (this.size() === 'lg') classes.push('w-6 h-6');

    return classes.join(' ');
  });

  readonly periodButtonClasses = computed(() => {
    const base =
      'cursor-pointer -m-2 px-3 rounded-xl border-1 focus:outline-none';

    const hoverClassPm = `hover:bg-sky-800`;
    const hoverClassAm = `hover:bg-amber-800`;

    const sizeClass =
      this.size() === 'sm'
        ? 'text-xs p-0.25'
        : this.size() === 'md'
        ? 'text-sm p-0.5'
        : 'text-base p-0.75';
    const periodClass =
      this.period() === 'PM'
        ? 'bg-sky-900 border-sky-900'
        : 'bg-amber-900 border-amber-900';

    return `${base} ${
      this.period() === 'PM' ? hoverClassPm : hoverClassAm
    } ${hoverClassAm} ${sizeClass} ${periodClass}`;
  });

  readonly periodTextClasses = computed(() => {
    const baseClasses = 'font-semibold text-white';
    const fontSize =
      this.size() === 'sm'
        ? 'text-xs'
        : this.size() === 'md'
        ? 'text-sm'
        : 'text-md';

    return `${baseClasses} ${fontSize}`;
  });

  // --- COMPUTED DISPLAY VALUE ---
  readonly displayValue = computed(() => {
    const v = this.cleanValue();
    if (this.isFocused()) {
      return v; // When editing, show raw numbers
    }

    if (v.length < 3) return v; // Show incomplete numbers as-is

    const hourPartLength = v.length - 2;
    let h = parseInt(v.substring(0, hourPartLength), 10);
    let m = parseInt(v.substring(hourPartLength), 10);
    if (isNaN(m) || m > 59) m = 59;
    if (isNaN(h)) h = 0;
    const h12 = h >= 13 ? (h % 12 || 12) : (h === 0 ? 12 : h);

    return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  });

  // --- Event Handlers ---
  onFocus(): void {
    this.isFocused.set(true);
  }

  onBlur(): void {
    this.isFocused.set(false);
    this.formatAndEmit();
    this.onTouched();
  }

  onTimeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value.replace(/[^0-9]/g, '').slice(0, 4);
    
    if (raw.length >= 3) {
      const hourPartLength = raw.length - 2;
      const typedH = parseInt(raw.substring(0, hourPartLength), 10);
      if (typedH >= 13) {
        // Automatically switch to PM for 24-hour hour inputs (e.g., 1645)
        this.period.set('PM');
        const h12 = typedH % 12 || 12;
        const mStr = raw.substring(hourPartLength);
        this.cleanValue.set(`${String(h12).padStart(2, '0')}${mStr}`);
        return;
      }
    }
    
    this.cleanValue.set(raw);
  }

  private formatAndEmit(): void {
    const v = this.cleanValue();
    if (v.length < 3) {
      this.onChange(null);
      return;
    }

    const hourPartLength = v.length - 2;
    let h = parseInt(v.substring(0, hourPartLength), 10);
    let m = parseInt(v.substring(hourPartLength), 10);

    if (isNaN(m) || m > 59) m = 59;
    if (isNaN(h)) h = 0;

    let h24: number;
    let h12: number;
    if (h >= 13) {
      this.period.set('PM');
      h24 = h > 23 ? 23 : h;
      h12 = h24 % 12 || 12;
    } else if (h === 0) {
      this.period.set('AM');
      h24 = 0;
      h12 = 12;
    } else {
      h12 = h;
      if (this.period() === 'PM') {
        h24 = h === 12 ? 12 : h + 12;
      } else {
        h24 = h === 12 ? 0 : h;
      }
    }

    this.cleanValue.set(`${String(h12).padStart(2, '0')}${String(m).padStart(2, '0')}`);
    const modelValue = `${String(h24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    this.onChange(modelValue);
  }

  clear() {
    this.cleanValue.set('');
    this.onChange(null);
    this.onTouched();
  }

  togglePeriod() {
    if (this.isDisabled()) return;
    this.period.update((p) => (p === 'AM' ? 'PM' : 'AM'));
    if (this.cleanValue().length >= 3) {
      this.formatAndEmit();
    }
  }

  // --- ControlValueAccessor implementation ---
  onChange: (value: string | null) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(modelValue: string | null): void {
    if (modelValue && /^\d{1,2}:\d{2}$/.test(modelValue)) {
      const [hStr, mStr] = modelValue.split(':');
      const h24 = parseInt(hStr, 10);
      const m = parseInt(mStr, 10);
      const period: 'AM' | 'PM' = h24 >= 12 ? 'PM' : 'AM';
      let h12 = h24 % 12;
      if (h12 === 0) h12 = 12;
      this.period.set(period);
      this.cleanValue.set(`${String(h12).padStart(2, '0')}${String(m).padStart(2, '0')}`);
    } else {
      this.cleanValue.set('');
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }
}
